import type { SyncErrorClassification } from '../domain/classify-sync-error.js';
import { ITEM_DEADLINE_MAX_MS } from '../domain/item-deadline.js';
import type { OutboxHandler, OutboxHandlerContext } from '../domain/outbox-handler.js';
import { type OutboxItem, transition } from '../domain/outbox-item.js';
import type { DexieOutboxRepository } from '../infrastructure/dexie-outbox.repository.js';

export interface DrainerIdentity {
  orgId: string;
  capturedByUserId: string;
}

export interface ForegroundDrainerDeps {
  repo: DexieOutboxRepository;
  handler: OutboxHandler<'checkin.create'>;
  buildContext: (item: OutboxItem) => OutboxHandlerContext;
  classify: (err: unknown) => SyncErrorClassification;
  computeBackoff: (attempts: number) => number;
  now: () => Date;
  /**
   * Second adversarial review, LOW #9 — swept after each item that
   * successfully syncs, so IndexedDB doesn't grow forever. Optional
   * (backward compatible with existing tests/callers that don't care about
   * blob cleanup) — omitted entirely is a safe no-op.
   */
  cleanupSyncedBlobs?: () => Promise<number>;
  /**
   * MEDIUM fix (adversarial review) — bounded outbox retention: swept
   * after each item that successfully syncs, same call site/rationale as
   * `cleanupSyncedBlobs` above, so the outbox table (not just its blobs)
   * doesn't grow forever either. Optional (backward compatible) — omitted
   * entirely is a safe no-op.
   */
  purgeOldOutbox?: () => Promise<number>;
  /**
   * Third adversarial review finding M1 — needs-auth recovery must only
   * fire when an access token/session ACTUALLY exists right now. A cached
   * `identity` (read from the offline profile) can be non-null even in the
   * genuinely-offline-shell case (no session at all) — recovering
   * needs-auth → pending in that case would just re-fail with another 401,
   * defeating the entire point of needs-auth (stop hammering a dead token).
   * Optional — omitted defaults to `true` (backward compatible with every
   * existing test/caller that doesn't model session state at all).
   */
  hasSession?: () => boolean;
  /**
   * Finding #4 (shared-device adversarial review) — returns the CURRENT
   * live session's identity (orgId + capturedByUserId derived from the
   * real, live auth store), or `null` when there is no live session right
   * now. Used to re-validate the identity actually driving each tick
   * INSTEAD OF the (possibly stale, cached-offline-profile) `identity`
   * argument `tick()` was called with — see `ensureLiveSession` below for
   * why this matters on a device shared by two operators. Optional —
   * omitted keeps the pre-existing identity-argument-only behavior exactly.
   */
  getLiveIdentity?: () => DrainerIdentity | null;
  /**
   * Finding #4 (shared-device adversarial review) — proactively recovers a
   * session (the SAME cookie-based bootstrap `main.tsx`/
   * `useOfflineShellRecovery` use) BEFORE any item is even selected, when
   * `hasSession()` reports there isn't one yet. On a device shared by two
   * operators, the `identity` `tick()` is called with can come from a
   * CACHED offline profile (operator A) while the browser's live httpOnly
   * refresh cookie actually belongs to a DIFFERENT operator (B) who signed
   * in more recently on the same device. Without resolving this UP FRONT,
   * the drainer would select A's items (they match the identity it was
   * called with), attempt to send one, and that item's own 401 would
   * silently bootstrap B's session mid-send — retrying A's item with B's
   * token, wrongly rejected server-side (the backend has no concept of "an
   * item can belong to a different org than the token sending it"; it just
   * 404s/403s). Resolving the live identity FIRST — before any item is
   * chosen — means the item-selection filter below always uses who this
   * device is ACTUALLY authenticated as right now, never a stale guess.
   * Optional — omitted keeps the pre-existing identity-argument-only
   * behavior exactly (backward compatible with every caller/test that
   * doesn't model a shared device at all).
   */
  ensureLiveSession?: () => Promise<void>;
  /**
   * pwa-offline-first Slice 7, task 7.9 (design §9 observability) — fired
   * after EVERY sync attempt outcome. All observe* deps are optional
   * (backward compatible with every existing caller/test) and deliberately
   * carry ONLY the classified error CODE, never any request/response body
   * — no plates, no phones, no names ever reach this callback. The
   * concrete Sentry wiring lives in `foreground-drainer-instance.ts` (the
   * composition root) via `infrastructure/observability.ts` — this module
   * never imports `@sentry/react` directly.
   */
  observeSyncOutcome?: (event: {
    kind: 'success' | 'retry' | 'reject' | 'needs-auth';
    errorCode?: string;
  }) => void;
  /** A terminal rejection — a distinct, aggregable Sentry EVENT (not just a breadcrumb), fingerprinted by code. */
  observeRejectedItem?: (code: string) => void;
  /** Fifth adversarial review precedent — surface a dropped-incident-photos pattern in aggregate. */
  observeIncidentPhotosDropped?: (code: string) => void;
}

/**
 * STUCK_SYNCING_MARGIN_MS — added on top of the LARGEST possible per-item
 * deadline (fourth adversarial review LOW #3). Covers the gap between "the
 * item's own `AbortSignal` deadline fires" and "the handler/drainer has
 * actually finished unwinding and written the row back to `pending`" — a
 * few seconds in practice, kept generous.
 */
const STUCK_SYNCING_MARGIN_MS = 60_000;

/**
 * STUCK_SYNCING_RESET_THRESHOLD_MS (second adversarial review HIGH #5;
 * fourth adversarial review LOW #3 raised it) — a `syncing` row older than
 * this (measured from its own `updatedAt`, stamped the instant `sync-start`
 * fired) is assumed to belong to a stalled fetch that never got a chance to
 * transition out (a crashed tab, a dead connection, etc.) and is reset back
 * to `pending` at the START of the very next tick — before that tick's own
 * single-flight lock would otherwise just sit blocked behind it forever.
 *
 * MUST stay strictly ABOVE the LARGEST possible per-item deadline
 * (`ITEM_DEADLINE_MAX_MS`, offline/domain/item-deadline.ts — now SCALED by
 * photo count, finding MEDIUM #2, no longer a small fixed constant): when
 * Web Locks is unavailable (older Safari — `foreground-drainer.ts`'s own
 * `drainWithCrossTabLock` fallback), nothing else prevents a stuck-syncing
 * reset from firing WHILE a legitimately still-running send (up to that max
 * deadline) is in flight in another tab — which would let a SECOND tick
 * start processing the SAME item concurrently.
 */
export const STUCK_SYNCING_RESET_THRESHOLD_MS = ITEM_DEADLINE_MAX_MS + STUCK_SYNCING_MARGIN_MS;

export interface ForegroundDrainer {
  tick(identity: DrainerIdentity): Promise<void>;
}

/**
 * createForegroundDrainer (coordinator finding #1, adversarial-review fix —
 * "no check-in ever reaches the server") — a MINIMAL foreground drainer.
 * Slice 7 owns the FULL SyncEngine (triggers catalogue, single-flight via
 * `navigator.locks`, FIFO, 3-photos-in-parallel, full error taxonomy,
 * needs-auth banner, quota guard, observability). This is deliberately
 * narrower: enough that an enqueued check-in actually reaches the server
 * this slice, following the SAME state machine (`transition()`) Slice 7
 * will keep using, so nothing here needs to be re-architected later — only
 * extended.
 *
 * Contract:
 * - Processes `pending` items FIFO by `seq`, scoped to `identity` (never an
 *   item captured under a different org/operator — those stay `pending`/
 *   `held`, untouched, exactly as designed for a shared device).
 * - Respects `nextAttemptAt` (backoff) — an item whose backoff hasn't
 *   elapsed yet is skipped this tick, not retried early.
 * - In-tab single-flight: a `tick()` call while one is already running is a
 *   silent no-op (not queued, not duplicated) — the NEXT trigger (online
 *   event, periodic tick, next enqueue) will pick up wherever this one left
 *   off, since Dexie is the durable source of truth.
 * - On success: `sync-success` → `synced`, result stored (official ticket
 *   number, replacing the provisional code live via CheckinSuccessScreen's
 *   own `useLiveQuery`).
 * - On a network/5xx/429 error: `sync-retry` → back to `pending`, attempts
 *   incremented, `nextAttemptAt` set via `computeBackoff(attempts)`.
 * - On a 401: `needs-auth` — and the REST of this tick's batch is skipped
 *   (no point hammering a dead token), but nothing is paused permanently:
 *   the next trigger (e.g. after a re-login) simply tries again.
 * - On any other 4xx: `sync-reject` → `rejected`, the error code/status
 *   stored for Slice 7's own RejectedItemSheet to read later.
 * - NEVER deletes an item, in any outcome.
 */
export function createForegroundDrainer(deps: ForegroundDrainerDeps): ForegroundDrainer {
  let inFlight = false;

  async function tick(identity: DrainerIdentity): Promise<void> {
    if (inFlight) return;
    inFlight = true;
    try {
      await drainWithCrossTabLock(identity);
    } finally {
      inFlight = false;
    }
  }

  async function drainWithCrossTabLock(identity: DrainerIdentity): Promise<void> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (locks?.request) {
      // Cross-tab single-flight where the browser supports the Web Locks
      // API. `ifAvailable: true` — a tab that doesn't get the lock simply
      // skips this tick rather than queuing (matches the in-tab semantics
      // above: the next trigger tries again).
      await locks.request('jq4-offline-foreground-drainer', { ifAvailable: true }, async (lock) => {
        if (!lock) return;
        await drainLoop(identity);
      });
    } else {
      // No Web Locks support (older Safari) — in-tab single-flight (the
      // `inFlight` guard above) is the only safety net; the idempotency
      // fingerprint (Slice 1) is what makes a genuine cross-tab double-send
      // harmless even without this lock.
      await drainLoop(identity);
    }
  }

  async function drainLoop(identity: DrainerIdentity): Promise<void> {
    const nowIso = deps.now().toISOString();

    // Finding #4 (shared-device adversarial review) — resolve who this
    // device is ACTUALLY authenticated as, proactively, before selecting
    // any item. Only when there is no session yet (`hasSession()` false) —
    // a device that already has a real, live session got there through a
    // normal login by the CURRENT operator, so there is no cross-operator
    // cookie ambiguity to resolve. See `ensureLiveSession`'s own doc
    // comment for the full shared-device race this closes.
    if (!(deps.hasSession?.() ?? true)) {
      await deps.ensureLiveSession?.();
    }
    // `effectiveIdentity` drives every decision below — the LIVE identity
    // when one is known, falling back to the (possibly stale) `identity`
    // tick() was called with only when no live-identity source is wired up
    // at all (backward compatible with every existing caller/test).
    const effectiveIdentity = deps.getLiveIdentity?.() ?? identity;

    // BLOCKER #3 — recover needs-auth items for the SAME ORG back to
    // pending BEFORE this tick's own pending pass, so a re-login/session
    // restore (which fires a tick via useForegroundDrainerTriggers'
    // identity-change effect) actually drains them the same tick, instead
    // of leaving them stuck until some later unrelated trigger. Org-scoped
    // per finding #6 — never recovers a DIFFERENT org's items.
    //
    // Third adversarial review finding M1 — ONLY when a session actually
    // exists right now (`hasSession()`). A cached `identity` can be
    // non-null even in the genuinely-offline-shell case (no session at
    // all); recovering in that case would just re-fail with another 401,
    // defeating the entire point of needs-auth.
    if (deps.hasSession?.() ?? true) {
      const needsAuthItems = (await deps.repo.byState('needs-auth')).filter(
        (item) => item.orgId === effectiveIdentity.orgId,
      );
      for (const item of needsAuthItems) {
        await deps.repo.put(transition(item, { type: 'login-same-org' }));
      }
    }

    // HIGH #5 — reset stuck syncing rows (a stalled fetch that never got a
    // chance to transition out) back to pending, so they're retried THIS
    // tick instead of sitting blocked forever. Modeled as a synthetic
    // sync-retry (increments attempts like a real retry would) with
    // nextAttemptAt = now, so it's immediately eligible below.
    const stuckSyncingItems = (await deps.repo.byState('syncing')).filter(
      (item) =>
        item.orgId === effectiveIdentity.orgId &&
        deps.now().getTime() - new Date(item.updatedAt).getTime() >
          STUCK_SYNCING_RESET_THRESHOLD_MS,
    );
    for (const item of stuckSyncingItems) {
      await deps.repo.put(
        transition(item, {
          type: 'sync-retry',
          error: { code: 'stale_syncing_reset', message: 'Stuck syncing row reset by drainer' },
          nextAttemptAt: nowIso,
        }),
      );
    }

    // MEDIUM #6 (identity policy correction, design D12) — items from ANY
    // operator of the SAME ORG are synced by whichever same-org operator is
    // logged in; only a DIFFERENT org's items stay untouched. `capturedAt`/
    // `capturedByUserId` stay claimed for audit only (see outbox-item.ts).
    const items = (await deps.repo.byState('pending'))
      .filter((item) => item.orgId === effectiveIdentity.orgId)
      .filter((item) => !item.nextAttemptAt || item.nextAttemptAt <= nowIso)
      .sort((a, b) => a.seq - b.seq);

    for (const item of items) {
      const stop = await processItem(item);
      if (stop) return;
    }
  }

  /** Returns true when the REST of this batch should be skipped (needs-auth). */
  async function processItem(item: OutboxItem): Promise<boolean> {
    const syncing = transition(item, { type: 'sync-start' });
    await deps.repo.put(syncing);

    try {
      const ctx = deps.buildContext(item);
      const result = await deps.handler.execute(item, ctx);
      await deps.repo.put(transition(syncing, { type: 'sync-success', result }));
      // LOW #9 — sweep now-synced blobs so IndexedDB doesn't grow forever.
      await deps.cleanupSyncedBlobs?.();
      // MEDIUM fix (adversarial review) — bounded outbox retention, same
      // call site.
      await deps.purgeOldOutbox?.();
      deps.observeSyncOutcome?.({ kind: 'success' });
      if (result.incidentPhotosDropped) {
        deps.observeIncidentPhotosDropped?.(result.incidentPhotosDropped.code);
      }
      return false;
    } catch (err) {
      const classified = deps.classify(err);

      if (classified.kind === 'needs-auth') {
        await deps.repo.put(transition(syncing, { type: 'needs-auth' }));
        deps.observeSyncOutcome?.({ kind: 'needs-auth', errorCode: classified.error.code });
        return true;
      }

      if (classified.kind === 'reject') {
        await deps.repo.put(transition(syncing, { type: 'sync-reject', error: classified.error }));
        deps.observeSyncOutcome?.({ kind: 'reject', errorCode: classified.error.code });
        deps.observeRejectedItem?.(classified.error.code);
        return false;
      }

      const nextAttemptAt = new Date(
        deps.now().getTime() + deps.computeBackoff(item.attempts),
      ).toISOString();
      let next = transition(syncing, {
        type: 'sync-retry',
        error: classified.error,
        nextAttemptAt,
      });
      // Fourth adversarial review, MEDIUM #2 — a `photo_token_invalid`
      // means our OWN client-side expiry estimate for at least one cached
      // key was wrong (or the token was invalidated some other way): every
      // entry in `uploadedPhotoKeys` is now untrustworthy, not just
      // whichever one the server happened to name, so the retry must
      // re-upload everything fresh rather than keep reusing a cache that
      // just proved itself unreliable.
      if (classified.error.code === 'photo_token_invalid') {
        next = { ...next, uploadedPhotoKeys: undefined };
      }
      await deps.repo.put(next);
      deps.observeSyncOutcome?.({ kind: 'retry', errorCode: classified.error.code });
      return false;
    }
  }

  return { tick };
}
